import { useCallback, useContext, useEffect, useState } from 'react';
import { Flex } from 'antd';
import { useFormik } from 'formik';

import { MainForm } from '@Components/index';
import FormItems from '@Components/MainForm/components/FormItems/FormItems';
import { usePostSalaryFormMutation } from '@Features/EmployeeSelfService/requests/SalaryCertificate/store/api/salaryCertificateApiSlice';
import {
  getSalaryCertificateTypeOptions,
  salaryCertificateCreateRequisitionInfo,
} from '@Features/EmployeeSelfService/requests/SalaryCertificate/store/selectors/salaryCertificateSelector';
import { salaryCertificateActions } from '@Features/EmployeeSelfService/requests/SalaryCertificate/store/salaryRequestSlice';
import {
  useAppDispatch,
  useAppSelector,
  useNavigationToListRequests,
} from '@Hooks/index';
import { useSubmitHrmRequestMutation } from '@Store/api/basicHrmApiSlice';
import { currentUserSelector } from '@Store/selectors/userInfoSelector';
import {
  RequestError,
  SalaryCertificateFormPostRequest,
} from '@Types/api/form';
import { validateForm } from '@Features/utils/validateForm';
import { NotificationContext } from '@Context/NotificationContext';
import { FooterActions } from '@Enum/index';

import { SalaryCertificateFormValues } from '../types';
import RequestDetailsSection from '../components/RequestDetailsSection';
import { initialValuesForm } from '../initialValuesForm';
import { SalaryCertificateFormSchema } from '../validationSchema';

const descriptionAlert = (
  <p>
    Please enter all the details and submit the data. After submission Salary
    Certificate request will go to higher officials approval and you can cancel
    it at any point of time before it gets approved by HR & PRO departments.
    <br />
    Each request will be monitored by HR & PRO department.
  </p>
);

export const SalaryCertificateCreateForm: React.FC = () => {
  const {
    saveSuccessNotification,
    errorNotification,
    submitSuccessNotification,
  } = useContext(NotificationContext);
  const salaryTypeOptions = useAppSelector(getSalaryCertificateTypeOptions);
  const currentUser = useAppSelector(currentUserSelector);
  const requisitionInfo = useAppSelector(
    salaryCertificateCreateRequisitionInfo
  );
  const dispatch = useAppDispatch();
  const [postSalaryForm] = usePostSalaryFormMutation();
  const [submitForm] = useSubmitHrmRequestMutation();
  const [disabledSaveForm, setDisableSaveForm] = useState(false);
  const { handleBackToListRequests } = useNavigationToListRequests();

  useEffect(
    () => () => {
      dispatch(salaryCertificateActions.setSalaryCertificate(null));
    },
    []
  );

  const handleSubmitForm = useCallback(
    (values: SalaryCertificateFormValues) => {
      const submitSalaryForm = async (): Promise<void> => {
        let body = {
          empNumber: currentUser.empNumber,
          leaveTypeID: Number(values.purpose),
          startDate: values.requiredByDate,
          destination: values.language,
          remarks: values.remarks,
        } as SalaryCertificateFormPostRequest;

        if (requisitionInfo?.requisitionNo) {
          body = {
            ...body,
            requestId: Number(requisitionInfo?.requisitionNo),
          };
        }

        setDisableSaveForm(true);

        try {
          const {
            createdDate,
            reqStatusId,
            requestId: reqId,
          } = await postSalaryForm(body).unwrap();

          dispatch(
            salaryCertificateActions.setSalaryCertificate({
              createdDate,
              requestId: reqId,
              reqStatusId,
            })
          );

          await submitForm(String(reqId)).unwrap();

          submitSuccessNotification(String(reqId));

          setDisableSaveForm(false);

          handleBackToListRequests();
        } catch (error) {
          const message = (error as RequestError)?.data;
          errorNotification(message);

          setDisableSaveForm(false);
        }
      };

      submitSalaryForm();
    },
    [currentUser, requisitionInfo, handleBackToListRequests]
  );

  const formik = useFormik({
    initialValues: initialValuesForm,
    enableReinitialize: true,
    validationSchema: SalaryCertificateFormSchema,
    onSubmit: handleSubmitForm,
  });

  const handleSaveAsDraft = useCallback(() => {
    const saveAsDraft = async (): Promise<void> => {
      const body = {
        empNumber: currentUser.empNumber,
        leaveTypeID: Number(formik.values.purpose),
        startDate: formik.values.requiredByDate,
        destination: formik.values.language,
        remarks: formik.values.remarks,
      };

      setDisableSaveForm(true);

      try {
        const {
          requestId: reqId,
          createdDate,
          reqStatusId,
        } = await postSalaryForm(body).unwrap();

        dispatch(
          salaryCertificateActions.setSalaryCertificate({
            createdDate,
            requestId: reqId,
            reqStatusId,
          })
        );

        saveSuccessNotification(String(reqId));

        setDisableSaveForm(false);

        handleBackToListRequests();
      } catch (error) {
        const message = (error as RequestError)?.data;
        errorNotification(message);

        setDisableSaveForm(false);
      }
    };

    const saveAsDraftHandler = async (): Promise<void> => {
      const isFormValid = await validateForm(
        SalaryCertificateFormSchema,
        formik
      );

      if (!isFormValid) {
        return;
      }

      await saveAsDraft();
    };

    saveAsDraftHandler();
  }, [currentUser, formik, handleBackToListRequests]);

  const handleFooterAction = useCallback(
    (action: FooterActions) => {
      if (action === FooterActions.SAVE_AS_DRAFT) {
        handleSaveAsDraft();
      }

      if (action === FooterActions.BACK_TO_REQUESTS) {
        handleBackToListRequests();
      }
    },
    [handleBackToListRequests, handleSaveAsDraft]
  );

  return (
    <Flex gap={10} vertical style={{ height: '100%' }}>
      <MainForm
        handleSubmit={formik.handleSubmit}
        userInfo={currentUser}
        requisitionInfo={requisitionInfo}
        onFooterAction={handleFooterAction}
        descriptionAlert={descriptionAlert}
        disabledSaveForm={disabledSaveForm}
        formType="create"
        showExtraUserFields
      >
        <FormItems>
          <RequestDetailsSection
            label="Request Details"
            formik={formik}
            formApproval={false}
            salaryTypeOptions={salaryTypeOptions}
          />
        </FormItems>
      </MainForm>
    </Flex>
  );
};

export default SalaryCertificateCreateForm;
